// Cross-platform PDF download helper — works in web browsers, iOS
// Capacitor WebView and Android Capacitor WebView. The plain `<a download>`
// trick fails on native because blob URLs don't survive across handoffs to
// the OS PDF viewer, so on native we convert the blob to a base64 data
// URL and open it via the Capacitor `Browser` plugin which lets the OS
// handle the PDF natively (Safari on iOS, Chrome on Android).
async function blobToBase64(blob) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = reader.result || "";
      const idx = typeof s === "string" ? s.indexOf(",") : -1;
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    reader.onerror = () => reject(reader.error || new Error("PDF read failed"));
    reader.readAsDataURL(blob);
  });
}

function normalizeBlob(input) {
  if (input instanceof Blob) return input;
  if (input instanceof ArrayBuffer) return new Blob([input], { type: "application/pdf" });
  if (typeof input === "string" && input.length > 0) {
    const clean = input.replace(/^data:application\/pdf;base64,/, "");
    try {
      const binary = atob(clean);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new Blob([bytes], { type: "application/pdf" });
    } catch (e) {
      throw new Error("Contenu PDF illisible");
    }
  }
  throw new Error("Expected a Blob");
}

function isNativePlatform() {
  return (
    typeof window !== "undefined" &&
    window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === "function" &&
    window.Capacitor.isNativePlatform()
  );
}

export async function downloadOrOpenPdfBlob(input, filename) {
  // Normalise input to a Blob. Depending on the axios adapter (native
  // Capacitor vs browser fetch), the response body may already be a Blob,
  // an ArrayBuffer, or even a base64 string — cover all three so callers
  // never have to think about it.
  const blob = normalizeBlob(input);

  if (isNativePlatform()) {
    const b64 = await blobToBase64(blob);
    const dataUrl = `data:application/pdf;base64,${b64}`;
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url: dataUrl });
      return;
    } catch (err) {
      window.location.href = dataUrl;
      return;
    }
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || "document.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
  }
}

/**
 * Share a PDF blob via the OS-level share sheet — on native (iOS/Android),
 * the file is first written to the Cache directory (`@capacitor/filesystem`)
 * then handed to `@capacitor/share` which opens the native share sheet
 * (Save to Files, Mail, iMessage, WhatsApp, ...). On web, we try the Web
 * Share API v2 (files); if unsupported, we fall back to the download
 * helper above so the user still gets the file.
 */
export async function sharePdfBlob(input, filename, opts = {}) {
  const blob = normalizeBlob(input);
  const safeName = filename || "document.pdf";
  const title = opts.title || "Course Saor";
  const text = opts.text || "Voici mon bon de commande / facture Saor.";

  if (isNativePlatform()) {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([
      import("@capacitor/filesystem"),
      import("@capacitor/share"),
    ]);
    const b64 = await blobToBase64(blob);
    const write = await Filesystem.writeFile({
      path: safeName,
      data: b64,
      directory: Directory.Cache,
      recursive: true,
    });
    const uri = write.uri;
    await Share.share({
      title,
      text,
      url: uri,
      dialogTitle: title,
    });
    return;
  }

  // Web : try Web Share API v2 with files, then fall back to a normal
  // download so the user still ends up with the PDF.
  try {
    const file = new File([blob], safeName, { type: "application/pdf" });
    if (
      typeof navigator !== "undefined" &&
      navigator.canShare &&
      navigator.share &&
      navigator.canShare({ files: [file] })
    ) {
      await navigator.share({ title, text, files: [file] });
      return;
    }
  } catch (_) { /* fall through */ }

  await downloadOrOpenPdfBlob(blob, safeName);
}
